import { useEffect, useState } from 'react'

import { faCalendar, faClock, faHistory, faLock } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  Badge,
  Box,
  Button,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import Avatar, { ICON_COLOR } from '@scoir/avatar'
import { Info, Tooltip } from '@scoir/tooltips'
import theme from 'material/theme'
import { Link } from 'react-router-dom'

import RegisterAction from 'components/VisitActions/RegisterAction'
import RequestAction from 'components/VisitActions/RequestAction'
import UnregisterAction from 'components/VisitActions/UnregisterAction'
import WithdrawAction from 'components/VisitActions/WithdrawAction'
import { useAppSelector } from 'store'
import { selectUserId, selectUserScid } from 'store/user'

import useGetCalendarVisitsData, { VisitsData } from './useGetCalendarVisitsData'
import { HighschoolData } from './useGetHighschoolPreviewData'

const useStyles = makeStyles({
  table: {
    border: `1px solid ${theme.palette.divider}`,
    '& .MuiTableCell-root': {
      border: `1px solid ${theme.palette.divider}`,
    },
    '& .MuiTableCell-head': {
      width: 'calc(100% / 4)',
    },
  },
  registerButton: {
    textAlign: 'left',
  },
  redButton: {
    color: theme.palette.error.main,
    textAlign: 'left',
  },
})

const VisitsDetails = ({ highschool }: { highschool: HighschoolData }) => {
  const classes = useStyles()
  const userId = useAppSelector(selectUserId)
  const userScid = useAppSelector(selectUserScid)
  const [visits, setVisits] = useState<VisitsData[]>([])
  const [visitAction, setVisitAction] = useState<'register' | 'request' | 'unregister' | 'withdrawal'>()
  const [event, setEvent] = useState<VisitsData>()
  const [refetch, setRefetch] = useState(false)

  const isPremiumExplorerEnabled = useAppSelector(
    (state) => state.college.record?.Features?.SupportsPremiumHighSchoolExplorer || false,
  )

  const { isSuccess, isError, data } = useGetCalendarVisitsData({
    highschoolId: highschool.id,
    refetch,
  })

  useEffect(() => {
    if (isSuccess) {
      setRefetch(false)
    }
  }, [isSuccess])

  useEffect(() => {
    if (data) {
      const adjustedVisits = data.map((visit) => {
        // Find Participants
        const mySchoolParticipants = visit?.Participants?.find((part) => part.Scid === userScid)
        // Check my user is a representative
        const myRep = mySchoolParticipants?.Representatives.find((rep) => rep.UserId === userId)
        // Assign state to display the correct visit action button
        if (myRep?.SystemState === 'confirmed_state') {
          visit.VisitButton = 'unregister'
        } else if (myRep?.SystemState === 'pending_state') {
          visit.VisitButton = 'withdrawal'
        } else if (visit.SystemState === 'open_state' && visit.ApprovalRequired) {
          visit.VisitButton = 'request'
        } else {
          visit.VisitButton = 'register'
        }
        return visit
      })
      // Sort visits by StartTime
      adjustedVisits.sort((a, b) => new Date(a.StartTime).getTime() - new Date(b.StartTime).getTime())
      // Only show first 3 visits
      const limitedVisits = adjustedVisits.slice(0, 3)
      setVisits(limitedVisits)
    }
  }, [data, userId, userScid])

  const viewLink = `selectedSchools=${encodeURIComponent(
    JSON.stringify([
      {
        id: highschool.id,
        name: highschool.name,
      },
    ]),
  )}`

  if (!highschool || !data) {
    return null
  }

  if (isError) {
    return (
      <Snackbar
        open={isError}
        message="There was an error getting the upcoming high school visits."
      />
    )
  }

  return (
    <Box
      display="flex"
      flexDirection="column"
      gridGap={16}
    >
      <Typography variant="h6">Visits</Typography>
      <Table
        className={classes.table}
        aria-label="visits table"
      >
        <TableHead>
          <TableRow>
            <TableCell>Date</TableCell>
            <TableCell>
              Time{' '}
              <Info
                outlinedIcon
                inheritColor
              >
                Events displayed in host's timezone
              </Info>
            </TableCell>
            <TableCell>Location</TableCell>
            <TableCell>Registration</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {visits.length === 0 ? (
            <TableRow>
              <TableCell
                colSpan={4}
                align="center"
              >
                <Box
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  gridGap={16}
                >
                  <Avatar
                    variant="circular"
                    aria-label="calendar icon"
                    iconColor={ICON_COLOR.default}
                    fallbackIcon={<FontAwesomeIcon icon={faCalendar} />}
                  />
                  <Typography>No upcoming visits </Typography>
                </Box>
              </TableCell>
            </TableRow>
          ) : (
            visits.map((row) => (
              <TableRow key={row.Id}>
                <TableCell>{Intl.DateTimeFormat('en-US').format(new Date(row.StartTime))}</TableCell>
                <TableCell>
                  {Intl.DateTimeFormat('en-US', {
                    hour: 'numeric',
                    minute: 'numeric',
                    timeZone: row.StartTimeZone,
                  }).format(new Date(row.StartTime))}{' '}
                  - <br />
                  {Intl.DateTimeFormat('en-US', {
                    hour: 'numeric',
                    minute: 'numeric',
                    timeZone: row.EndTimeZone,
                    timeZoneName: 'short',
                  }).format(new Date(row.EndTime))}
                </TableCell>
                <TableCell>{row.Virtual ? 'Virtual' : 'In-Person'}</TableCell>
                <TableCell>
                  {row.VisitButton === 'withdrawal' && (
                    <Button
                      variant="text"
                      className={classes.redButton}
                      onClick={() => {
                        setEvent(row)
                        setVisitAction('withdrawal')
                      }}
                    >
                      Withdraw Request
                    </Button>
                  )}
                  {row.VisitButton === 'request' && (
                    <Button
                      variant="text"
                      color="primary"
                      className={classes.registerButton}
                      onClick={() => {
                        setEvent(row)
                        setVisitAction('request')
                      }}
                    >
                      Request Registration
                    </Button>
                  )}
                  {row.VisitButton === 'unregister' && (
                    <Button
                      variant="text"
                      className={classes.redButton}
                      onClick={() => {
                        setEvent(row)
                        setVisitAction('unregister')
                      }}
                    >
                      Unregister
                    </Button>
                  )}
                  {row.VisitButton === 'register' && (
                    <Button
                      variant="text"
                      color="primary"
                      className={classes.registerButton}
                      onClick={() => {
                        setEvent(row)
                        setVisitAction('register')
                      }}
                    >
                      Register
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))
          )}
          <TableRow>
            <TableCell colSpan={4}>
              <Box
                display="flex"
                gridGap={8}
              >
                {visits.length > 0 && (
                  <Button
                    size="small"
                    variant="outlined"
                    color="primary"
                    startIcon={<FontAwesomeIcon icon={faCalendar} />}
                    component={Link}
                    to={`${import.meta.env.BASE_URL}/recruit/visits/view/week?focus=${
                      highschool.nextVisitDate
                    }&${viewLink}`}
                    disabled={!highschool?.nextVisitDate}
                  >
                    View All Availability
                  </Button>
                )}
                {isPremiumExplorerEnabled ? (
                  <Tooltip title="Coming Soon!">
                    <Badge
                      color="secondary"
                      badgeContent={<FontAwesomeIcon icon={faClock} />}
                    >
                      <Button
                        size="small"
                        variant="outlined"
                        color="primary"
                        startIcon={<FontAwesomeIcon icon={faHistory} />}
                        disabled
                      >
                        View Visit History
                      </Button>
                    </Badge>
                  </Tooltip>
                ) : (
                  <Badge
                    color="secondary"
                    badgeContent={<FontAwesomeIcon icon={faLock} />}
                  >
                    <Button
                      size="small"
                      variant="outlined"
                      color="primary"
                      startIcon={<FontAwesomeIcon icon={faHistory} />}
                      disabled
                    >
                      View Visit History
                    </Button>
                  </Badge>
                )}
              </Box>
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
      {/* Register / Request / Unregister / Withdrawal Modals */}
      <RegisterAction
        show={visitAction === 'register'}
        eventId={event?.Id || ''}
        onEventChange={() => {
          setVisitAction(undefined)
          setEvent(undefined)
          setRefetch(true)
        }}
        onClose={() => {
          setVisitAction(undefined)
          setEvent(undefined)
        }}
        highSchoolName={event?.HighSchoolName || ''}
        allowRepMeeting={(event?.Virtual || event?.AllowRepLocationType) && event?.AllowRepLocation}
        placeName={event?.Location?.PlaceName}
        allowRepLocationType={event?.AllowRepLocationType || false}
        meetingLink={event?.Location?.MeetingLink || ''}
      />
      <UnregisterAction
        show={visitAction === 'unregister'}
        eventId={event?.Id || ''}
        onEventChange={() => {
          setVisitAction(undefined)
          setEvent(undefined)
          setRefetch(true)
        }}
        onClose={() => {
          setVisitAction(undefined)
          setEvent(undefined)
        }}
      />
      <RequestAction
        show={visitAction === 'request'}
        eventId={event?.Id || ''}
        onEventChange={() => {
          setVisitAction(undefined)
          setEvent(undefined)
          setRefetch(true)
        }}
        onClose={() => {
          setVisitAction(undefined)
          setEvent(undefined)
        }}
        highSchoolName={event?.HighSchoolName || ''}
        allowRepMeeting={(event?.Virtual || event?.AllowRepLocationType) && event?.AllowRepLocation}
        placeName={event?.Location?.PlaceName}
        allowRepLocationType={event?.AllowRepLocationType || false}
      />
      <WithdrawAction
        show={visitAction === 'withdrawal'}
        eventId={event?.Id || ''}
        onEventChange={() => {
          setVisitAction(undefined)
          setEvent(undefined)
          setRefetch(true)
        }}
        onClose={() => {
          setVisitAction(undefined)
          setEvent(undefined)
        }}
        highSchoolName={event?.HighSchoolName || ''}
      />
    </Box>
  )
}

export default VisitsDetails
